import type {
  Block,
  StreamEvent,
  TextBlock,
  ReasoningBlock,
  ToolBlock,
  ErrorBlock,
} from './types';

export class BlockStore {
  private blocks: Map<string, Block> = new Map();

  /**
   * Handle a stream event and update the block store
   * Returns the created/updated block, or null if event was skipped
   */
  handleEvent(event: StreamEvent): Block | null {
    // Handle tool events first (they have special routing)
    if (event.type === 'tool-input-start' || event.type === 'tool_call_start') {
      return this.handleToolStart(event);
    } else if (event.type === 'tool-input-available') {
      return this.handleToolInput(event);
    } else if (event.type === 'tool-output-available' || event.type === 'tool_call_end') {
      return this.handleToolOutput(event);
    } else if (event.type === 'error') {
      return this.handleError(event);
    }

    // Route by event type suffix for text/reasoning
    if (event.type.endsWith('-start')) {
      return this.handleStart(event);
    } else if (event.type.endsWith('-delta')) {
      return this.handleDelta(event);
    } else if (event.type.endsWith('-end')) {
      return this.handleEnd(event);
    }

    return null;
  }

  private handleStart(event: StreamEvent): Block | null {
    if (!event.id) return null;

    const blockId = event.id;
    const existing = this.blocks.get(blockId);

    // Replay protection: skip if exists and complete
    if (existing?.isComplete) return null;

    const block = this.createBlock(event);
    if (block) {
      this.blocks.set(blockId, block);
      return block;
    }

    return null;
  }

  private createBlock(event: StreamEvent): Block | null {
    if (!event.id) return null;

    const now = Date.now();

    if (event.type === 'text-start') {
      const block: TextBlock = {
        id: event.id,
        type: 'text',
        role: (event.role as 'user' | 'assistant') || 'assistant',
        content: '',
        isComplete: false,
        createdAt: now,
        updatedAt: now,
      };
      return block;
    } else if (event.type === 'reasoning-start') {
      const block: ReasoningBlock = {
        id: event.id,
        type: 'reasoning',
        content: '',
        isComplete: false,
        createdAt: now,
        updatedAt: now,
      };
      return block;
    }

    return null;
  }

  private handleDelta(event: StreamEvent): Block | null {
    if (!event.id) return null;

    const blockId = event.id;
    const existing = this.blocks.get(blockId);

    // Skip if doesn't exist or is closed
    if (!existing || existing.isComplete) return null;

    // Only text and reasoning blocks support deltas
    if (existing.type !== 'text' && existing.type !== 'reasoning') return null;

    const delta = event.delta || '';
    const updated = {
      ...existing,
      content: existing.content + delta,
      updatedAt: Date.now(),
    };

    this.blocks.set(blockId, updated);
    return updated;
  }

  private handleEnd(event: StreamEvent): Block | null {
    if (!event.id) return null;

    const blockId = event.id;
    const existing = this.blocks.get(blockId);

    // Skip if doesn't exist or already closed (idempotent)
    if (!existing || existing.isComplete) return null;

    const completed = {
      ...existing,
      isComplete: true,
      updatedAt: Date.now(),
    } as Block;

    this.blocks.set(blockId, completed);
    return completed;
  }

  private handleToolStart(event: StreamEvent): Block | null {
    if (!event.toolCallId) return null;

    const toolId = event.toolCallId;
    const existing = this.blocks.get(toolId);

    // Replay protection: skip if exists and complete
    if (existing?.isComplete) return null;

    const now = Date.now();
    const block: ToolBlock = {
      id: event.id || toolId,
      type: 'tool',
      toolCallId: toolId,
      toolName: event.toolName || '',
      toolInput: null,
      isComplete: false,
      createdAt: now,
      updatedAt: now,
    };

    this.blocks.set(toolId, block);
    return block;
  }

  private handleToolInput(event: StreamEvent): Block | null {
    if (!event.toolCallId) return null;

    const toolId = event.toolCallId;
    const existing = this.blocks.get(toolId) as ToolBlock | undefined;

    // If block doesn't exist, we can't add input (need tool-input-start first)
    if (!existing) return null;

    if (existing.isComplete) return null;

    const updated: ToolBlock = {
      ...existing,
      toolInput: event.toolInput || event.input,
      updatedAt: Date.now(),
    };

    this.blocks.set(toolId, updated);
    return updated;
  }

  private handleToolOutput(event: StreamEvent): Block | null {
    if (!event.toolCallId) return null;

    const toolId = event.toolCallId;
    const existing = this.blocks.get(toolId) as ToolBlock | undefined;

    if (!existing) return null;

    const completed: ToolBlock = {
      ...existing,
      toolInput: existing.toolInput || event.toolInput || event.input,
      toolOutput: event.toolOutput || event.output,
      toolIsError: event.toolIsError || false,
      isComplete: true,
      updatedAt: Date.now(),
    };

    this.blocks.set(toolId, completed);
    return completed;
  }

  private handleError(event: StreamEvent): Block | null {
    const errorId = event.id || `error-${Date.now()}`;
    const now = Date.now();

    const block: ErrorBlock = {
      id: errorId,
      type: 'error',
      content: event.error || 'An unknown error occurred',
      errorType: event.errorType,
      isComplete: true,
      createdAt: now,
      updatedAt: now,
    };

    this.blocks.set(errorId, block);
    return block;
  }

  /**
   * Get all blocks (returns a copy for immutability)
   */
  getBlocks(): Map<string, Block> {
    return new Map(this.blocks);
  }

  /**
   * Clear all blocks
   */
  reset(): void {
    this.blocks.clear();
  }
}
